import React, { lazy, Suspense } from 'react';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SocketProvider } from './context/SocketContext';

import NavigationBar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import ScrollManager from './components/ScrollManager';



























import { useSocket } from './context/SocketContext';

const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Profile = lazy(() => import('./pages/Profile'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const OtpVerify = lazy(() => import('./pages/OtpVerify'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const AntiDiscrimination = lazy(() => import('./pages/AntiDiscrimination'));
const Reviews = lazy(() => import('./pages/Reviews'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const TechnicianDashboard = lazy(() => import('./pages/TechnicianDashboard'));
const TechnicianActivation = lazy(() => import('./pages/TechnicianActivation'));
const TechnicianOnboarding = lazy(() => import('./pages/TechnicianOnboarding'));
const TechnicianChat = lazy(() => import('./pages/TechnicianChat'));
const UserChat = lazy(() => import('./pages/UserChat'));
const SupportCenter = lazy(() => import('./pages/SupportCenter'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const AUTH_PATHS = ['/login', '/signup', '/technician-activate'];

const AppShell = () => {
  const { pathname } = useLocation();
  const { socket } = useSocket();
  const isAuth = AUTH_PATHS.includes(pathname);
  const isOnboarding = pathname.replace(/\/+$/, '') === '/join-as-technician';

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <ScrollManager />
      {!isAuth && !isOnboarding && <NavigationBar />}
      <main className="flex-grow-1">
        <RouteErrorBoundary>
        <Suspense fallback={<div className="container py-5" role="status">Loading page...</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/service/:id" element={<ServiceDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/join-as-technician" element={<TechnicianOnboarding />} />
          <Route path="/technician-activate" element={<TechnicianActivation />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-otp" element={<OtpVerify />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
          <Route path="/checkout" element={<Navigate to="/cart" replace />} />
          <Route path="/bookings" element={<PrivateRoute><Bookings /></PrivateRoute>} />
          <Route path="/technician" element={<PrivateRoute roles={['technician']}><TechnicianDashboard /></PrivateRoute>} />
          <Route path="/technician-chat" element={<PrivateRoute><SupportCenter socket={socket} /></PrivateRoute>} />
          <Route path="/legacy-technician-chat" element={<PrivateRoute><TechnicianChat /></PrivateRoute>} />
          <Route path="/support-chat" element={<PrivateRoute><SupportCenter socket={socket} /></PrivateRoute>} />
          <Route path="/support" element={<PrivateRoute><SupportCenter socket={socket} /></PrivateRoute>} />
          <Route path="/legacy-support-chat" element={<PrivateRoute><UserChat /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/anti-discrimination" element={<AntiDiscrimination />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:id" element={<BlogDetail />} />
          <Route path="*" element={<div className="container py-5"><h1>Page not found</h1><a href="/">Go to home</a></div>} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </main>
      {!isOnboarding && <Footer widgetOnly={isAuth || ['/support', '/support-chat', '/technician-chat'].includes(pathname)} />}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <SocketProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </SocketProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
